import { useApp } from '@context/AppContext';
import { useCorpse } from './useCorpse';
import { getZonePreset, ZONE_COLOUR_CLASSES } from './corpseAssignment';
import PlayerAvatar from '../shared/PlayerAvatar';

/**
 * Brief 3-second overlay shown between zones.
 * Announces who is drawing which zone, with a visual zone diagram.
 * Host auto-advances to drawing phase after 3s (handled in useCorpse).
 */
export default function CorpseAssigning() {
  const { state } = useApp();
  const { game, user, room } = state;
  // Hook must be active here so the assigning → drawing timer fires
  useCorpse();

  const zoneCount = game.zoneCount || 3;
  const preset = getZonePreset(zoneCount);
  const currentZone = game.currentZone ?? 0;
  const currentCreature = game.currentCreature ?? 0;
  const creature = game.creatures?.[currentCreature];
  const zoneName = preset[currentZone]?.name || `Zone ${currentZone + 1}`;
  const isDrawer = game.currentDrawerId === user.id;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="text-center animate-[fadeIn_0.3s_ease-out] max-w-sm mx-4">
        {/* Creature number label */}
        {(game.totalCreatures ?? 1) > 1 && (
          <p className="text-sm text-white/40 font-display mb-3">
            Creature {currentCreature + 1} of {game.totalCreatures}
          </p>
        )}

        {/* Main announcement */}
        {isDrawer ? (
          <>
            <p className="text-lg text-white/60 font-display mb-2">You&apos;re drawing the</p>
            <p className="text-4xl font-bold text-white font-display mb-4">
              {zoneName}
            </p>
            <p className="text-lg text-amber font-display animate-pulse">Get ready...</p>
          </>
        ) : (
          <>
            <p className="text-lg text-white/60 font-display mb-2">
              {room.members?.[game.currentDrawerId]?.nickname || 'Someone'} is drawing the
            </p>
            <p className="text-4xl font-bold text-white font-display mb-4">
              {zoneName}
            </p>
          </>
        )}

        {/* Zone diagram */}
        <div className="mt-6 flex flex-col items-center gap-1.5 w-32 mx-auto">
          {preset.map((zone, i) => {
            const colours = ZONE_COLOUR_CLASSES[zone.colour];
            const isCurrent = i === currentZone;
            const isDone = i < currentZone;

            return (
              <div
                key={zone.name}
                className={`w-full py-2.5 rounded-lg text-xs font-display font-medium transition-all ${
                  isCurrent
                    ? `${colours.highlight} text-white scale-110 shadow-lg`
                    : isDone
                      ? `${colours.bg} ${colours.text} opacity-60`
                      : 'bg-white/10 text-white/30'
                }`}
              >
                {zone.emoji} {zone.name}
                {isDone && ' ✓'}
              </div>
            );
          })}
        </div>

        {/* Zone assignments for this creature */}
        {creature && (
          <div className="mt-6 space-y-1.5">
            {preset.map((zone, zoneIdx) => {
              const zoneData = creature.zones?.[zoneIdx];
              if (!zoneData) return null;
              const isCurrent = zoneIdx === currentZone;
              const member = room.members?.[zoneData.assignedTo];
              const colours = ZONE_COLOUR_CLASSES[zone.colour];

              return (
                <div
                  key={zoneIdx}
                  className={`flex items-center justify-center gap-2 text-sm font-display ${
                    isCurrent ? 'text-white' : 'text-white/30'
                  }`}
                >
                  {member && (
                    <PlayerAvatar
                      nickname={member.nickname}
                      colour={member.colour}
                      size="sm"
                    />
                  )}
                  <span className={isCurrent ? 'font-semibold' : ''}>
                    {zoneData.assignedTo === user.id ? 'You' : zoneData.nickname}
                  </span>
                  <span className="text-white/20">→</span>
                  <span className={colours.text}>
                    {zone.name}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
